'use client';

import React from 'react';
import { useCookieConsent } from '../context/useCookieConsent';
import { CookieSettingsDialog } from './CookieSettingsDialog';
import { TranslateFn } from '../types';

interface CookieConsentBannerProps {
    t: TranslateFn;
    policyUrl: string;
}

const COOKIE_CONSENT_DESCRIPTION_KEY = 'cookieConsent.overallCookieConsentDescription';

const defaultDescriptionText =
    'We use cookies and similar technologies to operate our website, to analyze traffic and to enhance your user experience! ' +
    'We also share information collected through those cookies with our business partners. You can accept all cookies or visit ' +
    '<1>cookie policy</1> to adjust your preferences. For more information, please review our cookie policy.';

const outlineButtonClassName =
    'lg:w-max min-w-[130px] cursor-pointer rounded-md border border-[#E4E4E7] bg-transparent px-4 py-2 text-[#FAFAFA] hover:bg-white/10';

const primaryButtonClassName =
    'lg:w-max min-w-[100px] cursor-pointer rounded-md border-none bg-[#A3E635] px-4 py-2 text-[#09090B] hover:opacity-90';

const renderDescription = (rawDescription: string, policyUrl: string) => {
    const openTag = '<1>';
    const closeTag = '</1>';
    const openIndex = rawDescription.indexOf(openTag);
    const closeIndex = rawDescription.indexOf(closeTag);

    if (openIndex === -1 || closeIndex === -1 || closeIndex <= openIndex) {
        return rawDescription;
    }

    const before = rawDescription.slice(0, openIndex);
    const linkText = rawDescription.slice(openIndex + openTag.length, closeIndex);
    const after = rawDescription.slice(closeIndex + closeTag.length);

    return (
        <>
            {before}
            <a target="_blank" href={policyUrl} rel="noreferrer" className="text-white underline">
                {linkText}
            </a>
            {after}
        </>
    );
};

export function CookieConsentBanner({ t, policyUrl }: CookieConsentBannerProps) {
    const {
        showBanner,
        showSettingsDialog,
        consentStatus,
        openSettings,
        closeSettings,
        acceptAll,
        acceptOnlyNecessaryCookies,
        saveSettings,
    } = useCookieConsent();

    const handleOverlayClick = () => {
        closeSettings();
    };

    const handleOpenSettings = (event: React.MouseEvent) => {
        event.stopPropagation();
        openSettings();
    };

    const rawDescription = t(COOKIE_CONSENT_DESCRIPTION_KEY);
    const descriptionText =
        rawDescription === COOKIE_CONSENT_DESCRIPTION_KEY ? defaultDescriptionText : rawDescription;
    const description = renderDescription(descriptionText, policyUrl);

    if (!showBanner) {
        return null;
    }

    return (
        <div
            id="cookieConsent"
            onClick={handleOverlayClick}
            className="fixed inset-0 bg-[rgba(0,0,0,0.5)] text-base z-[9999]"
            style={{
                willChange: 'opacity',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                WebkitBackdropFilter: 'blur(5.5px)',
                backdropFilter: 'blur(5.5px)',
            } as React.CSSProperties}
            data-testid="cookie-consent-overlay"
        >
            {showSettingsDialog && (
                <div className="relative h-full overflow-y-auto flex items-center justify-center p-4">
                    <CookieSettingsDialog
                        t={t}
                        onClose={closeSettings}
                        onSave={saveSettings}
                        consentStatus={consentStatus}
                    />
                </div>
            )}

            {!showSettingsDialog && (
                <div
                    className="rounded-[10px] absolute bottom-6 lg:bottom-[50px] w-11/12 left-1/2 min-h-[100px] max-h-[calc(100dvh-3rem)] overflow-y-auto flex lg:justify-between justify-center flex-col lg:flex-row items-center px-5 py-6 lg:py-8 lg:px-12 box-border bg-[rgba(0,0,0,0.6)] text-white"
                    style={{
                        transform: 'translateX(-50%) translateZ(0)',
                        willChange: 'transform',
                        WebkitBackdropFilter: 'blur(2px)',
                        backdropFilter: 'blur(2px)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="cookie-consent-banner"
                >
                    <p className="mx-0 mt-0 lg:text-base text-sm">
                        {description}
                    </p>

                    <div className="flex flex-col lg:flex-row gap-4 lg:ml-9 mt-6 lg:mt-0 w-full lg:w-auto">
                        <button
                            type="button"
                            onClick={handleOpenSettings}
                            className={`${outlineButtonClassName} min-w-[130px]`}
                            data-testid="cookie-adjust-button"
                        >
                            {t('cookieConsent.adjustCookies')}
                        </button>

                        <button
                            type="button"
                            onClick={acceptOnlyNecessaryCookies}
                            className={`${outlineButtonClassName} min-w-[240px]`}
                            data-testid="cookie-accept-necessary-button"
                        >
                            {t('cookieConsent.acceptNecessary')}
                        </button>

                        <button
                            type="button"
                            onClick={acceptAll}
                            className={primaryButtonClassName}
                            data-testid="cookie-accept-all-button"
                        >
                            {t('cookieConsent.acceptAll')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CookieConsentBanner;
